/* components/patterns/chat/ChatEmptyState.jsx — пустой экран Ассистента + галерея скиллов (S5).
   Показывается, когда нет активного чата или активный чат без сообщений.
   Карточки: 3 самые востребованные функции + плитка «Все функции» (поиск по всему реестру).
   Клик по функции НЕ предзаполняет композер, а раскрывает справку ПРЯМО в области чата
   (вместо плиток, с кнопкой «Назад»): что отправить / примеры запросов.
   Пример можно нажать → подставится в чат (onUsePrompt). Источник: 06 §2.3. */

import React from 'react';
import { SearchInput } from '../../ui/Field.jsx';
import { Icon } from '../../ui/Icon.jsx';

  const { useState } = React;

  // Курируемая тройка «самых востребованных» функций (нет аналитики использования —
  // решение продуктовое). Если какой-то слаг отсутствует в реестре, добор из остальных.
  const FEATURED_SLUGS = ['ttk-generate', 'foodcost-optimize', 'menu-design'];

  function pickFeatured(skills) {
    const bySlug = new Map(skills.map((s) => [s.slug, s]));
    const picked = [];
    for (const slug of FEATURED_SLUGS) {
      const s = bySlug.get(slug);
      if (s && !picked.includes(s)) picked.push(s);
    }
    for (const s of skills) {
      if (picked.length >= 3) break;
      if (!picked.includes(s)) picked.push(s);
    }
    return picked.slice(0, 3);
  }

  function SkillCard({ skill, onPick }) {
    return (
      <button className="skill-card" onClick={() => onPick(skill)}>
        <span className="skill-card__icon"><Icon name={skill.icon || 'sparkles'} size={20} /></span>
        <span className="skill-card__body">
          <span className="skill-card__name">{skill.name}</span>
          <span className="skill-card__hint">{skill.hint}</span>
        </span>
        <Icon name="arrow-up-right" size={16} className="skill-card__go" />
      </button>
    );
  }

  function AllSkillsCard({ onClick }) {
    return (
      <button className="skill-card" onClick={onClick}>
        <span className="skill-card__icon"><Icon name="search" size={20} /></span>
        <span className="skill-card__body">
          <span className="skill-card__name">Все функции</span>
          <span className="skill-card__hint">список из всех возможностей с поиском</span>
        </span>
        <Icon name="arrow-up-right" size={16} className="skill-card__go" />
      </button>
    );
  }

  // секция справки: заголовок + буллеты
  function HelpSection({ title, icon, items }) {
    if (!items || !items.length) return null;
    return (
      <div className="skill-help__section">
        <div className="skill-help__section-title"><Icon name={icon} size={15} /> {title}</div>
        <ul className="skill-help__list">
          {items.map((it, i) => <li key={i}>{it}</li>)}
        </ul>
      </div>
    );
  }

  // тело справки: описание / что отправить / примеры (кликабельны).
  // Секцию «Что сделает Ресториум» (skill.outputs) убрали из справки намеренно.
  function SkillHelp({ skill, onUse }) {
    return (
      <div className="skill-help">
        {(skill.description || skill.hint) && (
          <p className="skill-help__lead">{skill.description || skill.hint}</p>
        )}
        <HelpSection title="Что отправить" icon="upload" items={skill.inputs} />
        {skill.examples && skill.examples.length > 0 && (
          <div className="skill-help__section">
            <div className="skill-help__section-title"><Icon name="chat" size={15} /> Примеры запросов</div>
            <div className="skill-help__hint">Нажмите пример — он подставится в поле чата</div>
            <div className="skill-help__examples">
              {skill.examples.map((ex, i) => (
                <button key={i} className="skill-example" onClick={() => onUse(ex)}>
                  <span className="skill-example__text">{ex}</span>
                  <Icon name="arrow-up-right" size={15} className="skill-example__go" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // обзор всех функций с поиском
  function SkillBrowse({ skills, query, setQuery, onPick }) {
    const q = query.trim().toLowerCase();
    const list = q
      ? skills.filter((s) => (`${s.name} ${s.hint} ${s.description || ''}`).toLowerCase().includes(q))
      : skills;
    return (
      <div className="skill-browse">
        <SearchInput value={query} onChange={setQuery} placeholder="Поиск функции…" />
        <div className="skill-browse__list">
          {list.length === 0 && <div className="skill-browse__empty t-caption text-mute">Ничего не нашли.</div>}
          {list.map((s) => (
            <button key={s.slug} className="skill-browse__row" onClick={() => onPick(s)}>
              <span className="skill-card__icon"><Icon name={s.icon || 'sparkles'} size={18} /></span>
              <span className="skill-browse__texts">
                <span className="skill-browse__name">{s.name}</span>
                <span className="skill-browse__hint">{s.hint}</span>
              </span>
              <Icon name="chevron-right" size={16} className="text-mute" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  function ChatEmptyState({ skills = [], onUsePrompt }) {
    const [selected, setSelected] = useState(null); // функция для справочной панели
    const [browse, setBrowse] = useState(false);    // открыт обзор всех функций
    const [query, setQuery] = useState('');

    const featured = pickFeatured(skills);

    const reset = () => { setSelected(null); setBrowse(false); setQuery(''); };
    const useExample = (text) => { reset(); onUsePrompt && onUsePrompt(text); };

    // Справка по функции — вместо плиток, с «Назад» (в обзор, если пришли из него; иначе в галерею).
    if (selected) {
      const back = browse ? () => setSelected(null) : reset;
      return (
        <div className="chat-empty">
          <div className="chat-empty__inner chat-empty__inner--detail">
            <button className="skill-detail__back" onClick={back}>
              <Icon name="chevron-left" size={16} /> {browse ? 'Все функции' : 'Назад'}
            </button>
            <div className="skill-detail__head">
              <span className="skill-card__icon"><Icon name={selected.icon || 'sparkles'} size={22} /></span>
              <div className="skill-detail__heading">
                <h2 className="skill-detail__title">{selected.name}</h2>
                {selected.hint && <p className="skill-detail__sub">{selected.hint}</p>}
              </div>
            </div>
            <SkillHelp skill={selected} onUse={useExample} />
          </div>
        </div>
      );
    }

    // Обзор всех функций — тоже вместо плиток, с «Назад» в галерею.
    if (browse) {
      return (
        <div className="chat-empty">
          <div className="chat-empty__inner chat-empty__inner--detail">
            <button className="skill-detail__back" onClick={reset}>
              <Icon name="chevron-left" size={16} /> Назад
            </button>
            <div className="skill-detail__head">
              <div className="skill-detail__heading">
                <h2 className="skill-detail__title">Все функции</h2>
                <p className="skill-detail__sub">Выберите функцию, чтобы узнать, как с ней работать</p>
              </div>
            </div>
            <SkillBrowse skills={skills} query={query} setQuery={setQuery} onPick={setSelected} />
          </div>
        </div>
      );
    }

    // Галерея: интро + 3 функции + «Все функции».
    return (
      <div className="chat-empty">
        <div className="chat-empty__inner">
          <div className="chat-empty__mark" aria-hidden="true">
            <img src="/logo-mark.svg" alt="" />
          </div>
          <h1 className="chat-empty__title">Что приготовим <em className="chat-empty__title-accent">сегодня</em>?</h1>
          <p className="chat-empty__lead">
            Ресториум собирает ТТК по ГОСТ, считает себестоимость и FC,
            проектирует меню и разбирает инвентаризации. Опишите задачу — или выберите функцию,
            чтобы узнать, как с ней работать.
          </p>
          {featured.length > 0 && (
            <div className="skill-grid">
              {featured.map((s) => <SkillCard key={s.slug} skill={s} onPick={setSelected} />)}
              <AllSkillsCard onClick={() => setBrowse(true)} />
            </div>
          )}
        </div>
      </div>
    );
  }

export { ChatEmptyState };
